import { useState } from "react";
import { X, Cookie, Check } from "lucide-react";
import { useCookieConsent } from "@/lib/cookieConsent";

const Toggle = ({ checked, disabled, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    data-testid={testId}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border transition-colors ${
      checked
        ? "bg-[color:var(--fk-gold)] border-[color:var(--fk-gold)]"
        : "bg-white/10 border-white/20"
    } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
        checked ? "translate-x-5" : "translate-x-0.5"
      } mt-[1px]`}
    />
  </button>
);

const Category = ({ title, description, checked, disabled, onChange, testIdSuffix }) => (
  <div
    data-testid={`cookie-category-${testIdSuffix}`}
    className="flex items-start justify-between gap-6 py-5 border-t border-white/10"
  >
    <div className="flex-1">
      <div className="font-display text-lg text-white mb-1">{title}</div>
      <p className="text-sm text-white/60 font-light leading-relaxed">
        {description}
      </p>
    </div>
    <Toggle
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      testId={`cookie-toggle-${testIdSuffix}`}
    />
  </div>
);

export const CookieBanner = () => {
  const { bannerOpen, consent, acceptAll, rejectAll, saveCustom } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [statistics, setStatistics] = useState(consent.statistics);
  const [marketing, setMarketing] = useState(consent.marketing);

  if (!bannerOpen) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-samtykke"
    >
      <div className="mx-auto max-w-4xl bg-[color:var(--fk-bg-2)] border border-white/10 shadow-2xl">
        {!showDetails ? (
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <Cookie className="w-6 h-6 text-[color:var(--fk-gold)] flex-shrink-0 mt-1" />
              <div className="flex-1">
                <div className="font-display text-xl md:text-2xl mb-3 text-white">
                  Vi bruger cookies
                </div>
                <p className="text-sm md:text-base text-white/70 font-light leading-relaxed">
                  Vi bruger nødvendige cookies for at siden fungerer, og gerne
                  statistik-cookies (Google Analytics) til at forbedre siden.
                  Du bestemmer selv — dit valg gemmes i din browser.
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                data-testid="cookie-customize"
                onClick={() => setShowDetails(true)}
                className="px-5 py-3 text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-white transition-colors"
              >
                Tilpas
              </button>
              <button
                type="button"
                data-testid="cookie-reject-all"
                onClick={rejectAll}
                className="px-6 py-3 text-[11px] tracking-[0.22em] uppercase border border-white/20 text-white hover:border-white/60 transition-colors"
              >
                Kun nødvendige
              </button>
              <button
                type="button"
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="px-6 py-3 text-[11px] tracking-[0.22em] uppercase bg-[color:var(--fk-gold)] text-black hover:bg-[color:var(--fk-gold-hi)] transition-colors font-medium"
              >
                Accepter alle
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 md:p-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="eyebrow mb-2 text-[color:var(--fk-gold)]">
                  Cookie-indstillinger
                </div>
                <div className="font-display text-xl md:text-2xl text-white">
                  Vælg dine præferencer
                </div>
              </div>
              <button
                type="button"
                data-testid="cookie-close-details"
                onClick={() => setShowDetails(false)}
                className="p-2 text-white/60 hover:text-white"
                aria-label="Tilbage"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <Category
              title="Nødvendige"
              description="Bruges til at siden fungerer korrekt — fx at huske dit cookie-valg og at kunne indsende formularer sikkert. Kan ikke slås fra."
              checked={true}
              disabled
              onChange={() => {}}
              testIdSuffix="necessary"
            />
            <Category
              title="Statistik"
              description="Google Analytics 4. Hjælper os med at forstå, hvordan folk bruger siden, så vi kan gøre den bedre. Data er anonymiseret (IP maskeret)."
              checked={statistics}
              onChange={setStatistics}
              testIdSuffix="statistics"
            />
            <Category
              title="Marketing"
              description="Google Tag Manager kan bruges til remarketing eller kampagnesporing. Ikke aktivt i dag, men klar hvis I senere vælger at annoncere."
              checked={marketing}
              onChange={setMarketing}
              testIdSuffix="marketing"
            />

            <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                data-testid="cookie-reject-all-details"
                onClick={rejectAll}
                className="px-6 py-3 text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-white transition-colors"
              >
                Afvis alle
              </button>
              <button
                type="button"
                data-testid="cookie-save-preferences"
                onClick={() => saveCustom({ statistics, marketing })}
                className="px-6 py-3 text-[11px] tracking-[0.22em] uppercase bg-[color:var(--fk-gold)] text-black hover:bg-[color:var(--fk-gold-hi)] transition-colors font-medium inline-flex items-center gap-2 justify-center"
              >
                <Check className="w-4 h-4" />
                Gem valg
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
